/*
 * Copyright (c) 2020
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, Input, AfterViewInit, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Report } from 'src/app/model';
import { MenuItem } from 'src/app/model/menu-item';
import { RightMenuService } from 'src/app/services';

@Component({
  selector: 'app-field-context-menu',
  templateUrl: 'field-context-menu.component.html',
  styleUrls: ['field-context-menu.component.scss']
})
export class FieldContextMenuComponent implements AfterViewInit {

  @Input() hostElement: HTMLElement;
  @Input() menuItems: MenuItem[];
  @Input() report: Report;

  private top = -100000;
  private left = -100000;

  constructor(
    private element: ElementRef,
    private cdr: ChangeDetectorRef,
    private rightMenuService: RightMenuService,
  ) {
  }

  ngAfterViewInit(): void {
    this.show();
    this.cdr.detectChanges();
  }

  show(): void {
    if (!this.hostElement)
      return;

    const p = this.positionElements(this.hostElement, this.element.nativeElement.children[0]);
    this.top = p.top;
    this.left = p.left;
  }

  hide(): void {
    this.top = -100000;
    this.left = -100000;
  }

  private positionElements(hostEl: HTMLElement, targetEl: HTMLElement): { top: number, left: number } {
    const hostElPos = this.position(hostEl);
    const targetElWidth = targetEl.offsetWidth;
    const targetElHeight = targetEl.offsetHeight;

    const shiftHeight: any = {
      center: function (): number {
        return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
      },
      top: function (): number {
        return hostElPos.top;
      },
      bottom: function (): number {
        return hostElPos.top + hostElPos.height;
      }
    };

    let targetElPos: { top: number, left: number };

    targetElPos = {
      top: shiftHeight['center'](),
      left: hostElPos.left - targetElWidth
    };

    return targetElPos;
  }

  private position(nativeEl: HTMLElement): { width: number, height: number, top: number, left: number } {
    let offsetParentBCR = { top: 0, left: 0 };
    const elBCR = this.offset(nativeEl);
    const offsetParentEl = this.parentOffsetEl(nativeEl);
    if (offsetParentEl !== window.document) {
      offsetParentBCR = this.offset(offsetParentEl);
      offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
      offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: elBCR.top - offsetParentBCR.top,
      left: elBCR.left - offsetParentBCR.left
    };
  }

  private offset(nativeEl: any): { width: number, height: number, top: number, left: number } {
    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: boundingClientRect.top + (window.pageYOffset || window.document.documentElement.scrollTop),
      left: boundingClientRect.left + (window.pageXOffset || window.document.documentElement.scrollLeft)
    };
  }

  private getStyle(nativeEl: HTMLElement, cssProp: string): string {
    if ((nativeEl as any).currentStyle) // IE
      return (nativeEl as any).currentStyle[cssProp];

    if (window.getComputedStyle)
      return (window.getComputedStyle(nativeEl) as any)[cssProp];

    return (nativeEl.style as any)[cssProp];
  }

  private isStaticPositioned(nativeEl: HTMLElement): boolean {
    return (this.getStyle(nativeEl, 'position') || 'static') === 'static';
  }

  private parentOffsetEl(nativeEl: HTMLElement): any {
    let offsetParent: any = nativeEl.offsetParent || window.document;
    while (offsetParent && offsetParent !== window.document && this.isStaticPositioned(offsetParent)) {
      offsetParent = offsetParent.offsetParent;
    }
    return offsetParent || window.document;
  }

  /**
   * Perform action when menu item is clicked
   * @param item
   */
  applyAction(item: any): void{
    let passObj = {};
    passObj['item'] = item;
    passObj['report'] = this.report;
    this.rightMenuService.triggerMenuItem(passObj);
  }
}
